import { Fragment, useContext } from 'react';
import {
  DevHubBox,
  DevHubChip,
  DevHubCircularProgress,
} from '@devxdiscover/devhub-ui';
import { Permission, Tag } from './types/InsightsTypes';
import { gql, useMutation } from '@apollo/client';
import AddTag from './AddTag';
import { SystemContext } from '../../contexts/SystemContext';

const DeleteTag = gql`
  mutation DeleteTag($input: RemoveTagInput!) {
    resources {
      tags {
        removeTag(input: $input) {
          success
        }
      }
    }
  }
`;

type Props = {
  tags: Tag[];
  edit: boolean;
  applicationId: string;
};

/**
 * Displays a list of application tags from ITRC
 * @param props props of the component
 * @param props.tags list of tags associated with an application
 */
function AppTags(props: Props) {
  const { GetApplicationQuery, applicationVariables, app } =
    useContext(SystemContext);

  const [deleteTag, { loading }] = useMutation(DeleteTag, {
    refetchQueries: [
      { query: GetApplicationQuery, variables: applicationVariables },
      'GetApplicationDetails',
    ],
  });

  const canEditTag = (tagId: number) => {
    const findTag = app?.authorizationPolicy?.find(
      (permission: Permission) =>
        permission?.policy === 'UpdateTagPolicy' &&
        permission?.data?.tagId === tagId,
    );

    return findTag?.hasAccess ?? false;
  };

  const handleDelete = (id: number) => {
    if (id) {
      const input = {
        applicationId: props?.applicationId,
        tagId: Number(id),
      };

      deleteTag({ variables: { input: input } });
    }
  };

  const tagComponent = (tag: Tag) => {
    if (props.edit) {
      return (
        <DevHubChip
          key={tag?.tag}
          label={tag?.tag}
          onDelete={() => handleDelete(tag?.id)}
          disabled={!canEditTag(tag?.id)}
        />
      );
    }
    return <DevHubChip key={tag?.tag} label={tag?.tag} />;
  };

  return (
    <Fragment>
      <DevHubBox>
        {props?.tags?.map((tag: Tag) => {
          return tagComponent(tag);
        })}
        {loading && <DevHubCircularProgress size={30} thickness={6} />}
      </DevHubBox>
      <DevHubBox>
        {props?.edit && <AddTag applicationId={props?.applicationId} />}
      </DevHubBox>
    </Fragment>
  );
}

export default AppTags;
